import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { createDriver } from "@/lib/api";

const EMPTY = { firstName: "", lastName: "", email: "", phone: "" };

/** New driver for a tenant; carriers' new drivers land on their company list automatically. */
export function AddDriverDialog({ open, onOpenChange, clientId, onAdded }) {
  const [v, setV] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setV((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!v.firstName.trim() || !v.lastName.trim() || !v.email.trim()) {
      toast.error("First name, last name and email are required.");
      return;
    }
    setBusy(true);
    try {
      await createDriver({ ...v, phone: v.phone || null, clientId });
      toast.success("Driver added.");
      setV(EMPTY);
      onOpenChange(false);
      onAdded();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  const field = (k, label, type = "text") => (
    <div className="space-y-1.5">
      <Label htmlFor={`add-driver-${k}`}>{label}</Label>
      <Input id={`add-driver-${k}`} type={type} value={v[k]} onChange={set(k)} data-testid={`add-driver-${k}`} />
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="add-driver-dialog">
        <DialogHeader>
          <DialogTitle>Add driver</DialogTitle>
          <DialogDescription>The driver doesn&apos;t get an email; they&apos;re matched to bookings by name and phone.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            {field("firstName", "First name *")}
            {field("lastName", "Last name *")}
          </div>
          {field("email", "Email *", "email")}
          {field("phone", "Mobile", "tel")}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="add-driver-cancel">Cancel</Button>
            <Button type="submit" disabled={busy} data-testid="add-driver-submit">{busy ? "Adding…" : "Add driver"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
